import type { Property } from '@/types/Property'
import type { MainNumberDto } from '@/types/PropertyDto'
import { formatPropertyHnr } from './format'

type PropertyHnr = Pick<
  NonNullable<Property['hnr']>,
  'municipalityNumber' | 'farmNumber' | 'holdingNumber' | 'leaseNumber'
>

interface PropertyDisplayMainNumberSource {
  cadastralNumber?: string
  mainNumber?: string
  municipality?: { number: string }
  farmNumber?: string
  propertyNumber?: string
  hnr?: PropertyHnr
}

const removeLeadingZeros = (num: string): string => {
  if (!num || num.trim() === '') return num
  const trimmed = num.trim()
  const parsed = parseInt(trimmed, 10)
  if (Number.isNaN(parsed)) return num
  return parsed.toString()
}

export const formatCadastralNumber = (cadastralNumber: string): string => {
  if (!cadastralNumber || cadastralNumber.trim() === '') {
    return cadastralNumber
  }

  const parts = cadastralNumber.split('-')
  if (parts.length < 2) {
    return cadastralNumber
  }

  const municipalityNumber = parts[0]
  const rest = parts.slice(1).join('-')

  const slashParts = rest.split('/')
  if (slashParts.length < 2) {
    return cadastralNumber
  }

  const farmNumber = removeLeadingZeros(slashParts[0])
  const holdingNumber = removeLeadingZeros(slashParts[1])

  if (slashParts.length === 2) {
    return `${municipalityNumber}-${farmNumber}/${holdingNumber}`
  }

  const leaseSection = slashParts[2]
  if (!leaseSection) {
    return `${municipalityNumber}-${farmNumber}/${holdingNumber}`
  }

  const leaseSectionParts = leaseSection.split('-')
  if (leaseSectionParts.length === 1) {
    const leaseNumber = removeLeadingZeros(leaseSectionParts[0])
    return `${municipalityNumber}-${farmNumber}/${holdingNumber}/${leaseNumber}`
  }

  const leaseNumber = removeLeadingZeros(leaseSectionParts[0])
  const sectionNumber = removeLeadingZeros(leaseSectionParts[1])
  return `${municipalityNumber}-${farmNumber}/${holdingNumber}/${leaseNumber}-${sectionNumber}`
}

export const toPropertyIdentityKey = (cadastralNumber: string): string => {
  const formatted = formatCadastralNumber(cadastralNumber).replace(/(\/\d+)\/0$/, '$1')
  const separatorIndex = formatted.indexOf('-')
  if (separatorIndex < 0) return formatted
  return `${removeLeadingZeros(formatted.slice(0, separatorIndex))}${formatted.slice(separatorIndex)}`
}

export const formatMainNumber = (mainNumber: MainNumberDto): string => {
  const municipalityNumber = mainNumber.municipalityNumber
  const farmNumber = removeLeadingZeros(mainNumber.farmNumber)
  const holdingNumber = removeLeadingZeros(mainNumber.holdingNumber)
  return `${municipalityNumber}-${farmNumber}/${holdingNumber}`
}

const formatRawPropertyHnr = (propertyNumber: string): string => {
  return formatPropertyHnr(formatCadastralNumber(propertyNumber))
}

const hasMunicipalityNumber = (
  hnr?: PropertyHnr,
): hnr is PropertyHnr & { municipalityNumber: number } =>
  hnr?.municipalityNumber !== null && hnr?.municipalityNumber !== undefined

const formatStructuredPropertyHnr = (
  property: PropertyDisplayMainNumberSource,
): string | undefined => {
  // Prefer the cadastral number: it reflects this specific property's real
  // holding number, which can differ from the property group's structured
  // main number (e.g. branch/leased properties sharing a farm number).
  if (property.cadastralNumber?.includes('/')) {
    return formatRawPropertyHnr(property.cadastralNumber)
  }

  if (property.mainNumber?.includes('/')) {
    return formatRawPropertyHnr(property.mainNumber)
  }

  if (property.farmNumber && property.propertyNumber) {
    return `${removeLeadingZeros(property.farmNumber)}/${removeLeadingZeros(property.propertyNumber)}`
  }

  if (property.hnr) {
    return `${property.hnr.farmNumber}/${property.hnr.holdingNumber}`
  }

  return undefined
}

const getMunicipalityNumber = (property: PropertyDisplayMainNumberSource): string | undefined => {
  if (property.municipality?.number) {
    return property.municipality.number
  }

  if (property.mainNumber?.includes('-')) {
    return property.mainNumber.split('-')[0]
  }

  if (property.cadastralNumber?.includes('-')) {
    return property.cadastralNumber.split('-')[0]
  }

  if (hasMunicipalityNumber(property.hnr)) {
    return property.hnr.municipalityNumber.toString()
  }

  return undefined
}

export const formatPropertyDisplayHnr = (property: PropertyDisplayMainNumberSource): string => {
  return formatStructuredPropertyHnr(property) ?? ''
}

export const formatPropertyDisplayMainNumber = (
  property: PropertyDisplayMainNumberSource,
): string => {
  const propertyHnr = formatPropertyDisplayHnr(property)
  const municipalityNumber = getMunicipalityNumber(property)

  if (propertyHnr && municipalityNumber) {
    return `${municipalityNumber}-${propertyHnr}`
  }

  if (propertyHnr) {
    return propertyHnr
  }

  if (property.mainNumber) {
    return formatCadastralNumber(property.mainNumber)
  }

  if (property.cadastralNumber) {
    return formatCadastralNumber(property.cadastralNumber)
  }

  return property.mainNumber ?? ''
}
